import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./styles.css";
import { VehicleService } from "../../../service/vehicle-service";
import { NUMBER } from "../../../constants/app-constants";
import { STEP_NAMES } from "../vehicle-information/constants";
import { yieldToMain } from "../../../utils/helpers/yield-to-main";
import { Label } from "@cars24/turbo-web/lib/label";
import { useTokens } from "@cars24/turbo-web/lib/theme-provider";
import { SearchInput } from "@cars24/turbo-web/lib/search-input";
import { sendCustomGaEvents } from "../../../tracking";
import { pdiManualFlowModel } from "../../../constants/tracking-constants/pre-delivery-inspection";
import IconSpinner from "../../shared/icon-spinner";

const ModelsList = ({
    brand,
    model,
    setDetailsConnect
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const { id: brandId } = brand || {};
    const { id: modelId } = model || {};
    const [searchText, setSearchText] = useState("");
    const [modelsData, setModelsData] = useState([]);
    const { tokens } = useTokens();

    const filteredModels = useMemo(() => {
        if (!searchText) {
            return modelsData;
        }
        return modelsData.filter((item) =>
            item.model_display.toLowerCase().includes(searchText.toLowerCase())
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, modelsData]);

    useEffect(() => {
        setIsLoading(true);
        if (brandId) {
            VehicleService.fetchModels(brandId)
                .then((response) => {
                    setModelsData(response?.data?.detail);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [brandId]);

    const handleSelectModel = async ({ name, id }) => {
        await yieldToMain();
        setDetailsConnect({
            [STEP_NAMES.Model]: { name, id },
            activeAccordionStepIndex: NUMBER.ONE,
            ownerName: ""
        });
        sendCustomGaEvents({
            ...pdiManualFlowModel,
            eventLabel: name
        });
    };

    const handleSelectModelClick = (modelData) => {
        handleSelectModel({
            name: modelData.model_display,
            id: modelData.model_id
        });
    };

    const popularModels = useMemo(
        () => filteredModels?.filter((item) => item.is_popular === "yes"),
        [filteredModels]
    );

    if (isLoading) return <div styleName={"styles.loadingIcon"}><IconSpinner/></div>;

    return (
        <div>
            <div styleName={"styles.searchField"}>
                <SearchInput placeholder="Search Model"  value={searchText}  onChange={(e) => setSearchText(e.target.value)} showCross={false}/>
            </div>
            {((popularModels && popularModels.length) || (filteredModels && filteredModels.length)) ?
                <React.Fragment>
                    <div styleName={"styles.listTitle"}>
                        <Label level={NUMBER.FOUR} color={tokens.text_default_text_secondary}>Popular Models</Label>
                    </div>
                    <ul styleName={"styles.brandsList"}>
                        {popularModels?.map((item, i) => (
                            <li
                                onClick={() => handleSelectModelClick(item)}
                                key={i}
                                styleName={item.model_id === modelId ? "styles.selected_state" : ""}
                                className="shrinkOnTouch"
                            >
                                <Label level={3}>{item.model_display}</Label>
                            </li>
                        ))}
                    </ul>
                    <div styleName={"styles.listTitle"}>
                        <Label level={NUMBER.FOUR} color={tokens.text_default_text_secondary}>All Models</Label>
                    </div>
                    <ul styleName={"styles.brandsList styles.allModels"}>
                        {filteredModels?.map((item, i) => (
                            <li
                                onClick={() => handleSelectModelClick(item)}
                                styleName={item.model_id === modelId ? "styles.selected_state" : ""}
                                key={i}
                            >
                                <Label level={3}>{item.model_display}</Label>
                            </li>
                        ))}
                    </ul>
                </React.Fragment> :
                <div styleName={"styles.listTitle"}>
                    <Label level={NUMBER.FOUR} color={tokens.text_default_text_secondary} fontWeight="regular">No results found</Label>
                </div>}
        </div>
    );
};

export default ModelsList;

ModelsList.propTypes = {
    setDetailsConnect: PropTypes.func,
    brand: PropTypes.object,
    model: PropTypes.object
};
